import { useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';

const KEYWORDS = new Set([
  'function', 'return', 'if', 'else', 'for', 'while', 'do', 'const', 'let', 'var', 'of', 'in',
  'break', 'continue', 'new', 'typeof', 'instanceof', 'switch', 'case', 'default', 'throw',
  'try', 'catch', 'finally', 'class', 'this', 'delete', 'void', 'yield', 'async', 'await',
]);
const LITERALS = new Set(['true', 'false', 'null', 'undefined', 'NaN', 'Infinity']);

const TOKEN_RE =
  /(\/\/[^\n]*|\/\*[\s\S]*?(?:\*\/|$))|("(?:[^"\\\n]|\\.)*"?|'(?:[^'\\\n]|\\.)*'?|`(?:[^`\\]|\\.)*`?)|(\b\d+(?:\.\d+)?\b)|([A-Za-z_$][\w$]*)|([^\sA-Za-z_$\d"'`/]+|\/)/g;

type Token = { text: string; cls?: string };

function tokenize(code: string): Token[] {
  const tokens: Token[] = [];
  let last = 0;
  for (const m of code.matchAll(TOKEN_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) tokens.push({ text: code.slice(last, idx) });
    const [text, comment, str, num, ident] = m;
    let cls: string | undefined;
    if (comment) cls = 'text-code-comment italic';
    else if (str) cls = 'text-code-string';
    else if (num) cls = 'text-code-number';
    else if (ident) {
      if (KEYWORDS.has(ident)) cls = 'text-code-keyword font-semibold';
      else if (LITERALS.has(ident)) cls = 'text-code-number';
      else if (code[idx + ident.length] === '(') cls = 'text-code-fn';
    } else cls = 'text-code-punct';
    tokens.push({ text, cls });
    last = idx + text.length;
  }
  if (last < code.length) tokens.push({ text: code.slice(last) });
  return tokens;
}

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  onRun?: () => void;
  className?: string;
  ariaLabel?: string;
}

const INDENT = '  ';

export function CodeEditor({ value, onChange, onRun, className, ariaLabel = 'Code editor' }: CodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const tokens = useMemo(() => tokenize(value), [value]);
  const lineCount = value.split('\n').length;

  const syncScroll = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (preRef.current) {
      preRef.current.scrollTop = ta.scrollTop;
      preRef.current.scrollLeft = ta.scrollLeft;
    }
    if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
  };

  const replaceRange = (start: number, end: number, text: string, cursorStart: number, cursorEnd = cursorStart) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.selectionStart = cursorStart;
      ta.selectionEnd = cursorEnd;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    const { selectionStart: start, selectionEnd: end } = ta;

    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onRun?.();
      return;
    }

    if (e.key === 'Tab' && !e.altKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      if (e.shiftKey) {
        // Outdent selected lines
        const block = value.slice(lineStart, end);
        const lines = block.split('\n');
        let removedFirst = 0;
        let removed = 0;
        const out = lines.map((l, i) => {
          const m = l.match(/^ {1,2}/);
          const n = m ? m[0].length : 0;
          if (i === 0) removedFirst = n;
          removed += n;
          return l.slice(n);
        });
        replaceRange(lineStart, end, out.join('\n'), Math.max(lineStart, start - removedFirst), end - removed);
      } else if (start !== end && value.slice(start, end).includes('\n')) {
        // Indent selected lines
        const block = value.slice(lineStart, end);
        const lines = block.split('\n');
        const out = lines.map((l) => INDENT + l).join('\n');
        replaceRange(lineStart, end, out, start + INDENT.length, end + INDENT.length * lines.length);
      } else {
        replaceRange(start, end, INDENT, start + INDENT.length);
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const line = value.slice(lineStart, start);
      const indent = line.match(/^\s*/)?.[0] ?? '';
      const prevChar = value[start - 1];
      const nextChar = value[end];
      if (prevChar === '{' || prevChar === '[' || prevChar === '(') {
        const inner = indent + INDENT;
        const closes = (prevChar === '{' && nextChar === '}') || (prevChar === '[' && nextChar === ']') || (prevChar === '(' && nextChar === ')');
        const text = closes ? `\n${inner}\n${indent}` : `\n${inner}`;
        replaceRange(start, end, text, start + 1 + inner.length);
      } else {
        replaceRange(start, end, `\n${indent}`, start + 1 + indent.length);
      }
      return;
    }

    if (e.key === '}' && start === end) {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const before = value.slice(lineStart, start);
      if (/^\s+$/.test(before) && before.length >= INDENT.length) {
        e.preventDefault();
        replaceRange(start - INDENT.length, end, '}', start - INDENT.length + 1);
      }
    }
  };

  const sharedText = 'font-mono text-[15px] leading-6 whitespace-pre tab-size-2';

  return (
    <div className={cn('relative flex overflow-hidden rounded-lg border bg-code-bg text-code-fg focus-within:ring-2 focus-within:ring-ring/60', className)}>
      <div
        ref={gutterRef}
        aria-hidden
        className={cn(sharedText, 'select-none overflow-hidden border-r border-code-border py-3 pl-3 pr-3 text-right text-code-comment')}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i}>{i + 1}</div>
        ))}
      </div>
      <div className="relative min-w-0 flex-1">
        <pre
          ref={preRef}
          aria-hidden
          className={cn(sharedText, 'pointer-events-none absolute inset-0 m-0 overflow-hidden p-3')}
        >
          {tokens.map((t, i) =>
            t.cls ? (
              <span key={i} className={t.cls}>
                {t.text}
              </span>
            ) : (
              <span key={i}>{t.text}</span>
            ),
          )}
          {'\n'}
        </pre>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={syncScroll}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          aria-label={ariaLabel}
          className={cn(
            sharedText,
            'relative block h-full w-full resize-none overflow-auto bg-transparent p-3 text-transparent caret-code-fg outline-none selection:bg-brand/40',
          )}
        />
      </div>
    </div>
  );
}
